import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  TextField,
  Card,
  CardContent,
  Rating,
  InputAdornment,
  IconButton,
  Paper,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import CarCard from '../components/CarCard';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const HeroSection = styled(motion.div)(({ theme }) => ({
  height: '90vh',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url('/images/hero-bg.jpeg')`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed', // Parallax effect
  display: 'flex',
  alignItems: 'center',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));

const SearchCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  maxWidth: 800,
  margin: '0 auto',
  marginTop: theme.spacing(4),
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  textAlign: 'center',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  maxWidth: 350,
  minWidth: 350,
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
  },
}));

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5 }
  }
};

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8B0000 30%, #A52A2A 90%)',
  borderRadius: theme.spacing(1),
  border: 0,
  color: 'white',
  height: 48,
  padding: '0 30px',
  boxShadow: '0 3px 5px 2px rgba(139, 0, 0, .3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #800000 30%, #8B0000 90%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 10px 2px rgba(139, 0, 0, .3)',
  }
}));

function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    location: '',
    pickupDate: '',
    returnDate: '',
  });

  const features = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'No Hidden Fees',
      description: 'Transparent pricing with all costs clearly displayed upfront.',
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: '24/7 Customer Support',
      description: 'Our dedicated team is always ready to assist you.',
    },
    {
      icon: <LocalOfferIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Best Price Guarantee',
      description: "Find a better price? We'll match it!",
    },
  ];

  const popularVehicles = [
    {
      id: 1,
      name: "Tesla Model 3",
      price: 15000,
      image: "/cars/tesla-model-3.jpg",
      transmission: "Automatic",
      fuelType: "Electric",
      type: "car",
      brand: "Tesla",
      model: "Model 3",
      ratings: {
        average: 4.8,
        count: 124
      }
    },
    {
      id: 2,
      name: "BMW S1000RR",
      price: 900,
      image: "/bikes/bmw-s1000rr.jpg",
      engineSize: "999cc",
      power: "206.6 BHP",
      type: "bike",
      brand: "BMW",
      model: "S1000RR",
      ratings: {
        average: 4.9,
        count: 89
      }
    },
    {
      id: 3,
      name: "Mercedes-Benz C-Class",
      price: 12000,
      image: "/cars/mercedes-c-class.jpg",
      transmission: "Automatic",
      fuelType: "Petrol",
      type: "car",
      brand: "Mercedes-Benz",
      model: "C-Class",
      ratings: {
        average: 4.7,
        count: 156
      }
    }
  ];

  const testimonials = [
    {
      name: 'John Doe',
      rating: 5,
      comment: 'Excellent service! The car was in perfect condition and the booking process was smooth.',
      avatar: '/avatars/user1.jpg',
      location: 'Dhaka',
      date: '2 days ago'
    },
    {
      name: 'Sarah Ahmed',
      rating: 5,
      comment: 'Amazing experience with RentWheels! The Tesla Model 3 was a dream to drive. Staff was very professional.',
      avatar: '/avatars/user2.jpg',
      location: 'Chittagong',
      date: '1 week ago'
    },
    {
      name: 'Mohammad Rahman',
      rating: 4,
      comment: 'Great selection of vehicles and competitive prices. Will definitely rent again!',
      avatar: '/avatars/user3.jpg',
      location: 'Sylhet',
      date: '2 weeks ago'
    },
    {
      name: 'Fatima Khan',
      rating: 5,
      comment: 'The customer service is outstanding. They helped me choose the perfect car for my family trip.',
      avatar: '/avatars/user4.jpg',
      location: 'Rajshahi',
      date: '3 days ago'
    },
    {
      name: 'Rahim Hasan',
      rating: 5,
      comment: 'Very transparent pricing and no hidden fees. The car was clean and well-maintained.',
      avatar: '/avatars/user5.jpg',
      location: 'Khulna',
      date: '5 days ago'
    }
  ];

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [vehiclesRef, vehiclesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Box sx={{ 
      mb: 8,
      mt: '-80px' // Negative margin to counteract the container margin
    }}>
      {/* Hero Section */}
      <motion.div
        ref={heroRef}
        initial="hidden"
        animate={heroInView ? "visible" : "hidden"}
        variants={staggerContainer}
      >
        <HeroSection>
          <Container>
            <motion.div variants={fadeInUp}>
              <Typography variant="h1">
                Find Your Perfect Ride
              </Typography>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Typography variant="h5" sx={{ mb: 4 }}>
                Luxury & Economy vehicles for any occasion
              </Typography>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <StyledButton
                variant="contained"
                size="large"
                onClick={() => navigate('/vehicles')}
                endIcon={<ArrowForwardIcon />}
              >
                Browse Vehicles
              </StyledButton>
            </motion.div>
          </Container>
        </HeroSection>
      </motion.div>

      {/* Features Section */}
      <Box sx={{ py: 8 }}>
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div variants={scaleIn}>
                    <FeatureCard elevation={2}>
                      {feature.icon}
                      <Typography variant="h6" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </FeatureCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Popular Vehicles Section */}
      <Box sx={{ py: 8, bgcolor: 'grey.50' }}>
        <Container>
          <motion.div
            ref={vehiclesRef}
            initial="hidden"
            animate={vehiclesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Typography variant="h3" align="center" gutterBottom>
              Popular Vehicles
            </Typography>
            <Typography 
              variant="h6" 
              align="center" 
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              Discover our most sought-after vehicles
            </Typography>
            <Grid container spacing={3}>
              {popularVehicles.map((vehicle) => (
                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ 
                      y: -10,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <CarCard vehicle={vehicle} />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <StyledButton
                variant="contained"
                size="large"
                onClick={() => navigate('/vehicles')}
                endIcon={<ArrowForwardIcon />}
              >
                View All Vehicles
              </StyledButton>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom>
          What Our Customers Say
        </Typography>
        <Box 
          sx={{ 
            display: 'flex', 
            overflowX: 'auto', 
            py: 2,
            px: 2,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.100',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'primary.main',
              borderRadius: 4,
            },
          }}
        >
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar 
                  src={testimonial.avatar} 
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    mr: 2,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }} 
                />
                <Box>
                  <Typography variant="h6">{testimonial.name}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {testimonial.location}
                  </Typography>
                  <Rating value={testimonial.rating} readOnly size="small" />
                </Box>
              </Box>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ mb: 2, fontStyle: 'italic' }}
              >
                "{testimonial.comment}"
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {testimonial.date}
              </Typography>
            </TestimonialCard>
          ))}
        </Box>
      </Container>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: 8, 
          bgcolor: 'primary.main',
          color: 'white'
        }}
      >
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <motion.div variants={fadeInUp}>
                  <Typography variant="h3" gutterBottom>
                    Ready to Get Started?
                  </Typography>
                  <Typography variant="h6">
                    Join thousands of satisfied customers
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={4}>
                <motion.div variants={fadeInUp}>
                  <StyledButton
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => navigate('/register')}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        transform: 'translateY(-2px)',
                      },
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}
                  >
                    Sign Up Now
                  </StyledButton>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', pt: 8, pb: 4 }}>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                About RentWheels
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Your trusted partner for car rentals. We provide quality vehicles and exceptional service.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <IconButton color="inherit">
                  <FacebookIcon />
                </IconButton>
                <IconButton color="inherit">
                  <TwitterIcon />
                </IconButton>
                <IconButton color="inherit">
                  <InstagramIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="h6" gutterBottom>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button color="inherit" sx={{ justifyContent: 'flex-start' }}>
                  About Us
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start' }}>
                  Contact
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start' }}>
                  Terms & Conditions
                </Button>
                <Button color="inherit" sx={{ justifyContent: 'flex-start' }}>
                  Privacy Policy
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" gutterBottom>
                Contact Us
              </Typography>
              <Typography variant="body2" paragraph>
                123 Main Street
                <br />
                City, Country 12345
              </Typography>
              <Typography variant="body2" paragraph>
                Phone: +1 234 567 890
                <br />
                Email: info@rentwheels.com
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" gutterBottom>
                Newsletter
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Subscribe to get special offers and updates.
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Your email"
                sx={{
                  bgcolor: 'grey.800',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'grey.700',
                    },
                    '&:hover fieldset': {
                      borderColor: 'grey.600',
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
              >
                Subscribe
              </Button>
            </Grid>
          </Grid>
          <Typography variant="body2" align="center" sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'grey.800' }}>
            © {new Date().getFullYear()} RentWheels. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default Home; 